// src/utils/viewHelpers.js

/**
 * 秒を HH:MM:SS または MM:SS 形式の文字列にフォーマットします。
 * @param {number} sec - 合計秒数
 * @returns {string} フォーマットされた時間文字列
 */
export const formatTimer = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * "MM:SS" または "HH:MM:SS" 形式の時間文字列を合計秒数に変換します。
 * @param {string} timeStr - 時間文字列
 * @returns {number} 合計秒数
 */
export const parseTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return 0;
  const parts = timeStr.split(':').map(Number);
  if (parts.some(isNaN)) return 0;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
};

/**
 * トーナメント詳細情報から平均スタックを計算します。
 * @param {object} details - トーナメント詳細オブジェクト
 * @returns {number} 計算された平均スタック
 */
export const calculateAverageStack = (details) => {
    if (!details.showPlayerStats) return 0;

    const totalChips =
      (details.initialEntries * details.initialStack) +
      (details.totalRebuys * details.rebuyStack) +
      (details.totalAddons * details.addonStack) -
      (details.forfeitedChips || 0);
    const remainingPlayers = Math.max(0,
      details.initialEntries - details.knockouts + details.totalRebuys
    );
    return remainingPlayers > 0 ? Math.floor(totalChips / remainingPlayers) : 0;
};

/**
 * ★ 新規追加: 現在時刻から指定秒数後のDateオブジェクトを計算する関数
 * @param {number} remainingSeconds - 残り秒数
 * @returns {Date} - 未来の日時を表すDateオブジェクト
 */
export const calculateFutureDate = (remainingSeconds) => {
  const now = new Date();
  const futureTimestamp = now.getTime() + remainingSeconds * 1000;
  return new Date(futureTimestamp);
};

const DATE_COLUMNS = new Set(["created_at", "createdAt", "updated_at", "updatedAt"]);

/**
 * 配列を指定されたカラムと順序（ASC / DESC）でソートして新しい配列を返します。
 * @param {Array<object>} items - ソート対象のオブジェクト配列
 * @param {string} sortColumn - ソート対象のキー名
 * @param {"ASC" | "DESC"} sortOrder - ソート順序 ("ASC" または "DESC")
 * @returns {Array<object>} ソートされた新しい配列
 */
export const sortItems = (items, sortColumn, sortOrder = "DESC") => {
  if (!Array.isArray(items)) return [];
  const copied = [...items];
  if (!sortColumn) return copied;

  const direction = sortOrder === "ASC" ? 1 : -1;
  const isDateCol = DATE_COLUMNS.has(sortColumn);

  return copied.sort((a, b) => {
    const valA = a?.[sortColumn];
    const valB = b?.[sortColumn];

    if (valA == null && valB == null) return 0;
    if (valA == null) return -1 * direction;
    if (valB == null) return 1 * direction;

    if (isDateCol) {
      const timeA = new Date(valA).getTime() || 0;
      const timeB = new Date(valB).getTime() || 0;
      return (timeA - timeB) * direction;
    }

    if (typeof valA === "number" && typeof valB === "number") {
      return (valA - valB) * direction;
    }

    return String(valA).localeCompare(String(valB)) * direction;
  });
};